import { useContext, useEffect, useState } from 'react';
import { Container, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Greeting from '@/components/calendar/daily/Greeting';
import DisplayDate from '@/components/calendar/daily/DisplayDate';
import CompletedDailyActivities from '@/components/calendar/daily/CompletedDailyActivities';
import DayOverview, { CalmCard } from '@/components/calendar/daily/DayOverview';
import MissedActivities from '@/components/calendar/daily/MissedActivities';
import OngoingActivities from '@/components/calendar/daily/OngoingActivities';
import UpcomingActivities from '@/components/calendar/daily/UpcomingActivities';
import { CalendarDayScene } from '@/contexts/CalendarDayContext';
import AiOnboarding from '@/components/utils/dialogs/AiOnboardingDialog';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import { CalendarContext } from '@/contexts/CalendarContext';
import { useCharacter } from '@/contexts/CharacterContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import StableActionRow from '@/components/layout/StableActionRow';
import CreateActivityFab from '@/components/layout/CreateActivityFab';
import { useNamedCompanionSpeech } from '@/components/character';

const DailyPage = () => {
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { refreshWeek } = useContext(CalendarContext);
  const { t } = useTranslation();
  const effectiveChildMode = useEffectiveChildMode();
  const { setMood, setCompanionSpeech } = useCharacter();
  // Kompisen tilltalar BARNET vid namn (user.firstName), inte sig själv.
  const { speechKey, speechVars } = useNamedCompanionSpeech('child.companion.homeIdle');

  // Hemskärmens idle-hälsning (endast barnläge). Sätts när röstläget är stängt
  // och nollställs vid unmount så att den inte fastnar i andra vyer.
  useEffect(() => {
    if (!effectiveChildMode || voiceOpen) {
      return;
    }
    setMood('happy');
    setCompanionSpeech(t(speechKey, speechVars) ?? '');
    return () => {
      setCompanionSpeech('');
      setMood('idle');
    };
  }, [effectiveChildMode, voiceOpen, speechKey, speechVars, setMood, setCompanionSpeech, t]);

  const handleVoiceSaved = () => {
    setVoiceOpen(false);
    refreshWeek();
  };

  return (
    <CalendarDayScene date={new Date()}>
      <Container>
        <Stack spacing={3} pb={12} pt={1}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: { mx: 4, mt: 2 },
            })}
          >
            <Greeting />
            <DisplayDate />
          </Stack>
          {/* Överblicken FÖRE listorna: en enda nästa handling och hur långt dagen har
              kommit. Vuxenvyn hade bara listorna, och det var det som gjorde sidan
              intetsägande — man fick läsa fyra rubriker för att veta vad som var på gång. */}
          <DayOverview />

          <StableActionRow
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: { mx: 4 },
            })}
            start={<CompletedDailyActivities />}
            end={<></>}
          />

          <MissedActivities />
          <OngoingActivities />
          <UpcomingActivities />

          {/* Lugnt avslut i stället för tom yta under listorna. */}
          <CalmCard />

          <AiOnboarding />
        </Stack>
      </Container>

      {/* Gemensam FAB-position (samma som barnvyn) */}
      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />

      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={handleVoiceSaved}
      />
    </CalendarDayScene>
  );
};

export default DailyPage;
